import React, { useState, useEffect, useRef } from 'react';
import axiosInstance from '../utils/axiosConfig';
import { formatMongoDate } from '../utils/dateUtils.js';  // Import date utilities
import "../styles/theme.css";
import "../styles/ViewAdminSchedules.css";
import "../styles/ViewSchedulesModern.css";
import ExportButton from './ExportButton';
import { exportData, exportRenderedTableData } from '../utils/exportUtils';
import LoadingSpinner from './LoadingSpinner';
import { useToast } from '../utils/toastContext';

// Reusable Off component for empty slots
const OffBadge = () => (
    <span style={{
        display: 'inline-block',
        padding: '6px 14px',
        background: 'linear-gradient(135deg, #e2e8f0 0%, #cbd5e1 100%)',
        color: '#475569',
        borderRadius: '8px',
        fontSize: '0.875rem',
        fontWeight: '600',
        border: '1px solid #cbd5e1',
        textAlign: 'center',
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
        letterSpacing: '0.5px',
        textTransform: 'uppercase'
    }}>
        🚫 Off
    </span>
);

// Modern Column Selector Component
const ColumnSelector = ({ availableColumns, visibleColumns, onColumnToggle, onExportTable }) => {
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef(null);

    // Close dropdown on outside click
    useEffect(() => {
        if (!isOpen) return;
        const handleClick = (e) => {
            if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClick);
        return () => document.removeEventListener('mousedown', handleClick);
    }, [isOpen]);

    const getColumnLabel = (column) => {
        const labels = {
            'rotation': 'Rotation',
            'preceptor': 'Preceptor',
            'copreceptor': 'Co-Preceptor',
            'email': 'Email',
            'specialty': 'Experience',
            'startDate': 'Start Date',
            'endDate': 'End Date'
        };
        return labels[column] || column.charAt(0).toUpperCase() + column.slice(1);
    };

    // Export button style matching AdminResourceCenter "Export Resources" button exactly
    const exportButtonStyle = {
        background: 'linear-gradient(135deg, #f7fbfc 0%, #edf6f8 100%)',
        color: '#134b5f',
        border: '1.5px solid #7aa7b6',
        borderRadius: 14,
        padding: '14px 32px',
        fontWeight: 600,
        fontSize: 16,
        letterSpacing: 0.5,
        cursor: 'pointer',
        transition: 'all 0.3s ease',
        outline: 'none',
        boxShadow: '0 10px 24px rgba(19, 75, 95, 0.12)',
        display: 'flex',
        alignItems: 'center',
        gap: '8px'
    };

    return (
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
            <div className="column-selector-container" style={{ position: 'relative' }} ref={dropdownRef}>
                <button 
                    className="column-selector-button"
                    onClick={() => setIsOpen(!isOpen)}
                    style={{
                        background: 'linear-gradient(135deg, #f7fbfc 0%, #edf6f8 100%)',
                        color: '#134b5f',
                        border: '1.5px solid #7aa7b6',
                        borderRadius: 14,
                        padding: '14px 32px',
                        fontWeight: 600,
                        fontSize: 16,
                        letterSpacing: 0.5,
                        cursor: 'pointer',
                        transition: 'all 0.3s ease',
                        outline: 'none',
                        boxShadow: '0 10px 24px rgba(19, 75, 95, 0.12)',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                    }}
                    onMouseEnter={(e) => {
                        e.target.style.transform = 'translateY(-2px)';
                        e.target.style.background = 'linear-gradient(135deg, #e4f4f7 0%, #ffffff 100%)';
                        e.target.style.boxShadow = '0 14px 28px rgba(19, 75, 95, 0.16)';
                    }}
                    onMouseLeave={(e) => {
                        e.target.style.transform = 'translateY(0)';
                        e.target.style.background = 'linear-gradient(135deg, #f7fbfc 0%, #edf6f8 100%)';
                        e.target.style.boxShadow = '0 10px 24px rgba(19, 75, 95, 0.12)';
                    }}
                >
                    <span style={{ fontSize: '1rem' }}>📊</span>
                    <span>Customize Columns</span>
                    <span style={{ 
                        fontSize: '0.75rem', 
                        transition: 'transform 0.2s',
                        transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)'
                    }}>▼</span>
                </button>
                {isOpen && (
                    <div style={{
                        position: 'absolute',
                        top: '100%',
                        right: '0',
                        background: '#fff',
                        border: '1px solid #e2e8f0',
                        borderRadius: '16px',
                        boxShadow: '0 20px 40px rgba(0, 0, 0, 0.15)',
                        zIndex: 1000,
                        minWidth: '320px',
                        maxHeight: '400px',
                        overflowY: 'auto',
                        marginTop: '0.5rem',
                        animation: 'modalFadeIn 0.2s ease-out'
                    }}>
                        <div style={{
                            background: 'linear-gradient(135deg, #2b4663 0%, #4a90e2 100%)',
                            padding: '1.25rem',
                            borderRadius: '16px 16px 0 0',
                            color: '#fff'
                        }}>
                            <h4 style={{
                                margin: 0,
                                fontSize: '1.1rem',
                                fontWeight: '700',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.5rem',
                                color: '#ffffff'
                            }}>
                                <span>🎯</span>
                                Select Table Columns
                            </h4>
                            <p style={{
                                margin: '0.5rem 0 0 0',
                                fontSize: '0.85rem',
                                opacity: 0.9
                            }}>
                                Choose which columns to display in your table
                            </p>
                        </div>
                        <div style={{ padding: '1.5rem' }}>
                            {availableColumns.map(column => (
                                <label key={column} style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '0.75rem',
                                    padding: '0.75rem',
                                    cursor: 'pointer',
                                    transition: 'all 0.2s ease',
                                    borderRadius: '10px',
                                    marginBottom: '0.5rem',
                                    background: visibleColumns.includes(column) ? '#f0f9ff' : 'transparent',
                                    border: `2px solid ${visibleColumns.includes(column) ? '#3b82f6' : 'transparent'}`
                                }}
                                onMouseEnter={(e) => {
                                    if (!visibleColumns.includes(column)) {
                                        e.target.style.background = '#f8fafc';
                                    }
                                }}
                                onMouseLeave={(e) => {
                                    if (!visibleColumns.includes(column)) {
                                        e.target.style.background = 'transparent';
                                    }
                                }}
                                >
                                    <input
                                        type="checkbox"
                                        checked={visibleColumns.includes(column)}
                                        onChange={() => onColumnToggle(column)}
                                        style={{
                                            width: '18px',
                                            height: '18px',
                                            cursor: 'pointer',
                                            accentColor: '#3b82f6'
                                        }}
                                    />
                                    <span style={{
                                        color: '#374151',
                                        fontSize: '0.95rem',
                                        fontWeight: visibleColumns.includes(column) ? '600' : '500'
                                    }}>
                                        {getColumnLabel(column)}
                                    </span>
                                </label>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

// --- SORTABLE TABLE HEADER COMPONENT ---
const SortableTableHeader = ({ label, columnKey, sortState, onSort, style = {} }) => {
    const isActive = sortState.column === columnKey;
    const direction = isActive ? sortState.direction : undefined;
    return (
        <th
            onClick={() => onSort(columnKey)}
            style={{
                ...style,
                cursor: 'pointer',
                userSelect: 'none',
                position: 'relative',
                transition: 'all 0.2s',
                color: '#fff',
                fontWeight: isActive ? '700' : '600',
                textShadow: isActive ? '0 2px 4px rgba(0, 0, 0, 0.5)' : '0 2px 4px rgba(0, 0, 0, 0.3)',
                padding: '14px 32px',
                boxShadow: '0 10px 24px rgba(19, 75, 95, 0.12)'
            }}
        >
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                {label}
                <span style={{ fontSize: '0.8rem', marginLeft: 4, display: 'inline-block', width: 16 }}>
                    {isActive ? (
                        direction === 'asc' ? '▲' : '▼'
                    ) : (
                        <span style={{ color: 'rgba(255, 255, 255, 0.5)' }}>▲▼</span>
                    )}
                </span>
            </span>
        </th>
    );
};

const ViewStudentSchedule = () => {
    const { showError } = useToast();
    const [originalScheduleData, setOriginalScheduleData] = useState({}); // Store original data
    const [currentCurriculums, setCurrentCurriculums] = useState([]); // Store current curriculums
    const [selectedCurriculum, setSelectedCurriculum] = useState('');
    const [availableCurriculums, setAvailableCurriculums] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [configurations, setConfigurations] = useState({});
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');

    useEffect(() => {
        if (startDate && endDate && endDate < startDate) {
            setEndDate('');
        }
    }, [startDate, endDate]);

    useEffect(() => {
        if (!error) return;
        showError(error);
        setError('');
    }, [error, showError]);
    
    // Column management state - now per table
    const [availableColumns] = useState(['rotation', 'preceptor', 'copreceptor', 'email', 'specialty', 'startDate', 'endDate']);
    const [visibleColumnsByTable, setVisibleColumnsByTable] = useState({});

    // --- SORTING STATE ---
    // Add sorting state for each curriculum table
    const [sortState, setSortState] = useState({});

    // Sorting handler
    const handleSort = (curriculumId, column) => {
        setSortState(prev => {
            const currentSort = prev[curriculumId] || { column: '', direction: 'asc' };
            const newDirection = currentSort.column === column && currentSort.direction === 'asc' ? 'desc' : 'asc';
            return {
                ...prev,
                [curriculumId]: { column, direction: newDirection }
            };
        });
    };

    // --- SORTING LOGIC ---
    function getSortedData(data, curriculumId) {
        const sortConfig = sortState[curriculumId];
        if (!sortConfig || !sortConfig.column) return data;
        
        const { column, direction } = sortConfig;
        
        const sorted = [...data].sort((a, b) => {
            let aValue, bValue;
            
            if (column === 'rotation') {
                aValue = a.rotation !== undefined ? parseInt(a.rotation) : 0;
                bValue = b.rotation !== undefined ? parseInt(b.rotation) : 0;
            } else if (column === 'preceptor') {
                aValue = a.preceptor?.name || '';
                bValue = b.preceptor?.name || '';
            } else if (column === 'copreceptor') {
                aValue = a.copreceptor?.name || '';
                bValue = b.copreceptor?.name || '';
            } else if (column === 'email') {
                aValue = a.preceptor?.email || '';
                bValue = b.preceptor?.email || '';
            } else if (column === 'specialty') {
                aValue = a.speciality || '';
                bValue = b.speciality || '';
            } else if (column === 'startDate') {
                aValue = a.rotationStartDate ? new Date(a.rotationStartDate).getTime() : 0;
                bValue = b.rotationStartDate ? new Date(b.rotationStartDate).getTime() : 0;
            } else if (column === 'endDate') {
                aValue = a.rotationEndDate ? new Date(a.rotationEndDate).getTime() : 0;
                bValue = b.rotationEndDate ? new Date(b.rotationEndDate).getTime() : 0;
            } else {
                aValue = a[column] || '';
                bValue = b[column] || '';
            }
            
            // Handle numeric vs string comparison
            if (typeof aValue === 'number' && typeof bValue === 'number') {
                return direction === 'asc' ? aValue - bValue : bValue - aValue;
            } else {
                const comparison = String(aValue).localeCompare(String(bValue));
                return direction === 'asc' ? comparison : -comparison;
            }
        });
        
        return sorted;
    }

    useEffect(() => {
        Promise.all([
            fetchAllSchedules(),
            fetchConfigurations()
        ]).catch(err => {
            console.error("Initial data loading error:", err);
            setError('Failed to load initial data');
            setLoading(false);
        });
    }, []);

    const fetchConfigurations = async () => {
        try {
            const response = await axiosInstance.get('/config/get_configurations/');
            setConfigurations(response.data);
            return response.data;
        } catch (err) {
            console.error("Configuration fetch error:", err);
            setError('Failed to fetch configurations');
            throw err;
        }
    };

    const fetchAllSchedules = async () => {
        setLoading(true);
        try {
            const response = await axiosInstance.get('/crud/student/schedule');
            const data = response.data;
            if (import.meta.env.VITE_DEBUG_MODE === 'true') console.log('All schedules:', data);
            
            // Extract current curriculum if available
            const currentCurriculum = data.currentCurriculum;
            const currentCurriculums = currentCurriculum ? [currentCurriculum] : [];
            setCurrentCurriculums(currentCurriculums);
            
            // Remove currentCurriculum from data to get only schedule data
            const { currentCurriculum: _, ...scheduleData } = data;
            
            setSchedulesByCurriculum(scheduleData);
            setOriginalScheduleData(scheduleData); // Store original data
            
            const curriculumIds = Object.keys(scheduleData);
            setAvailableCurriculums(curriculumIds);
            if (curriculumIds.length === 0) {
                setSelectedCurriculum('');
            }
            
            // Initialize column visibility for all tables
            const initialVisibility = {};
            curriculumIds.forEach(id => {
                initialVisibility[id] = [...availableColumns]; // Use spread to create a new array
            });
            setVisibleColumnsByTable(initialVisibility);
            
            setLoading(false);
            return data;
        } catch (err) {
            console.error("Schedule fetch error:", err);
            setError('Failed to fetch schedules');
            setLoading(false);
            throw err;
        }
    };

    // Add this helper function to format dates
    const formatDateForAPI = (dateString) => {
        if (!dateString) return null;
        const date = new Date(dateString);
        return `${String(date.getDate()).padStart(2, '0')}/${String(date.getMonth() + 1).padStart(2, '0')}/${date.getFullYear()}`;
    };

    // Helper function to parse dates from various formats
    const parseDate = (dateString) => {
        if (!dateString) return null;
        
        // Handle different date formats
        let date;
        if (typeof dateString === 'string') {
            if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
                const [year, month, day] = dateString.split('-').map(Number);
                date = new Date(year, month - 1, day);
            } else if (dateString.includes('/')) {
                const [day, month, year] = dateString.split('/');
                date = new Date(year, month - 1, day);
            } else {
                date = new Date(dateString);
            }
        } else {
            date = new Date(dateString);
        }
        
        return isNaN(date.getTime()) ? null : date;
    };    // Helper function to check if two date ranges intersect
    const dateRangesIntersect = (range1Start, range1End, range2Start, range2End) => {
        const start1 = parseDate(range1Start);
        const end1 = parseDate(range1End);
        const start2 = parseDate(range2Start);
        const end2 = parseDate(range2End);
        
        if (!start1 || !end1 || !start2 || !end2) return false;
        
        return start1 <= end2 && start2 <= end1;
    };

    // Frontend date range filtering function
    const filterSchedulesByDateRange = (filterStartDate, filterEndDate, curriculumId) => {
        if (!filterStartDate || !filterEndDate || !originalScheduleData || !curriculumId || !originalScheduleData[curriculumId]) {
            return {};
        }

        const filteredSchedules = {};
        const rotations = originalScheduleData[curriculumId];
        const filteredRotations = rotations.filter(rotation => {
            const rotationStart = parseDateValue(rotation.rotationStartDate);
            const rotationEnd = parseDateValue(rotation.rotationEndDate);

            if (!rotationStart || !rotationEnd) return false;

            return dateRangesIntersect(
                filterStartDate, filterEndDate,
                rotationStart, rotationEnd
            );
        });

        if (filteredRotations.length > 0) {
            filteredSchedules[curriculumId] = filteredRotations;
        }
        
        return filteredSchedules;
    };

    const handleCurriculumChange = (e) => {
        const newCurriculum = e.target.value;
        setSelectedCurriculum(newCurriculum);
        setStartDate(''); // Reset date filters
        setEndDate('');
        // Reset to original data when curriculum is selected
        setSchedulesByCurriculum(originalScheduleData);
    };

    // Helper function to get filtered curriculums based on selection
    const getFilteredCurriculums = () => {
        if (selectedCurriculum && originalScheduleData[selectedCurriculum]) {
            return { [selectedCurriculum]: originalScheduleData[selectedCurriculum] };
        }
        return originalScheduleData;
    };

    // Modify handleStartDateChange
    const handleStartDateChange = (e) => {
        const newStartDate = e.target.value;
        if (!selectedCurriculum) {
            setStartDate('');
            return;
        }
        setStartDate(newStartDate);
        
        if (newStartDate && endDate) {
            const filteredData = filterSchedulesByDateRange(newStartDate, endDate, selectedCurriculum);
            setSchedulesByCurriculum(filteredData);
            setAvailableCurriculums(Object.keys(filteredData));
        } else if (!newStartDate && !endDate) {
            setSchedulesByCurriculum(getFilteredCurriculums());
            setAvailableCurriculums(Object.keys(getFilteredCurriculums()));
        }
    };

    // Update handleEndDateChange
    const handleEndDateChange = (e) => {
        const newEndDate = e.target.value;
        if (!selectedCurriculum) {
            setEndDate('');
            return;
        }
        setEndDate(newEndDate);
        
        if (startDate && newEndDate) {
            const filteredData = filterSchedulesByDateRange(startDate, newEndDate, selectedCurriculum);
            setSchedulesByCurriculum(filteredData);
            setAvailableCurriculums(Object.keys(filteredData));
        } else if (!startDate && !newEndDate) {
            setSchedulesByCurriculum(getFilteredCurriculums());
            setAvailableCurriculums(Object.keys(getFilteredCurriculums()));
        }
    };

    // Parse API date (normalized ISO string, e.g. "2025-09-01T00:00:00.000Z")
    const parseDateValue = (value) => {
        if (value == null) return null;
        if (value instanceof Date) return isNaN(value.getTime()) ? null : value;
        if (typeof value === 'string') {
            if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
                const [year, month, day] = value.split('-').map(Number);
                const d = new Date(year, month - 1, day);
                return isNaN(d.getTime()) ? null : d;
            }
            const d = new Date(value);
            return isNaN(d.getTime()) ? null : d;
        }
        return null;
    };

    // Filter rotations based on date range - updated filtering logic  
    const getFilteredRotations = () => {
        if (startDate && endDate) {
            // Return the already filtered data from date change handlers
            return schedulesByCurriculum;
        } else if (selectedCurriculum && originalScheduleData[selectedCurriculum]) {
            // Return selected curriculum data
            return { [selectedCurriculum]: originalScheduleData[selectedCurriculum] };
        } else if (!selectedCurriculum && !startDate && !endDate) {
            // Return all data
            return originalScheduleData;
        }
        
        return {};
    };

    // Format date for display
    const formatDate = (dateString) => {
        if (!dateString) return 'Date not available';
        
        try {
            const formattedDate = formatMongoDate(dateString);
            return formattedDate !== 'Invalid Date' ? formattedDate : 'Invalid date';
        } catch (err) {
            console.error("Date formatting error:", err, "for date:", dateString);
            return 'Invalid date';
        }
    };

    const getRotationDates = (rotation) => {
        if (rotation.rotationStartDate && rotation.rotationEndDate) {
            const startDate = parseDateValue(rotation.rotationStartDate);
            const endDate = parseDateValue(rotation.rotationEndDate);
            
            return {
                start: startDate ? formatDate(startDate) : 'N/A',
                end: endDate ? formatDate(endDate) : 'N/A'
            };
        }
        
        // Fallback for old format
        if (rotation.start_date && rotation.end_date) {
            return {
                start: formatDate(rotation.start_date),
                end: formatDate(rotation.end_date)
            };
        }
                
        return { start: 'N/A', end: 'N/A' };
    };

    // Add helper function to get curriculum display name
    const getCurriculumDisplayName = (curriculumId) => {
        return curriculumId || 'Unknown Curriculum';
    };

    const renderCurriculumSection = (curriculumId, rotations) => {
        const visibleColumns = visibleColumnsByTable[curriculumId] || availableColumns;
        
        return (
            <div key={curriculumId} className="modern-schedule-table-container">
                <div className="modern-table-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <h3>Curriculum: {getCurriculumDisplayName(curriculumId)}</h3>
                    <ColumnSelector 
                        availableColumns={availableColumns}
                        visibleColumns={visibleColumns}
                        onColumnToggle={(column) => handleColumnToggle(curriculumId, column)}
                        onExportTable={() => handleTableExport(curriculumId)}
                    />
                </div>
                <table className="modern-schedule-table">
                    <thead>
                        <tr key={`header-${visibleColumns.join('-')}`} style={{
                            background: 'linear-gradient(90deg, #2b4663 0%, #3c5a7a 25%, #4a90e2 75%, #5ba3f5 100%)',
                            backgroundSize: '100% 100%',
                            boxShadow: '0 8px 32px rgba(43, 70, 99, 0.25)',
                            position: 'relative',
                            width: '100%',
                            display: 'table-row'
                        }}>
                            {visibleColumns.includes('rotation') && (
                                <SortableTableHeader 
                                    label="Rotation" 
                                    columnKey="rotation" 
                                    sortState={sortState[curriculumId] || { column: '', direction: 'asc' }} 
                                    onSort={(col) => handleSort(curriculumId, col)}
                                    style={{
                                        background: 'none !important',
                                        backgroundColor: 'transparent !important',
                                        padding: '1rem 1rem',
                                        textAlign: 'left',
                                        border: 'none',
                                        borderRight: 'none',
                                        fontSize: '0.95rem',
                                        letterSpacing: '0.75px',
                                        position: 'relative',
                                        textTransform: 'uppercase',
                                        fontFamily: 'system-ui, -apple-system, sans-serif',
                                        transition: 'none !important'
                                    }}
                                />
                            )}
                            {visibleColumns.includes('preceptor') && (
                                <SortableTableHeader 
                                    label="Preceptor" 
                                    columnKey="preceptor" 
                                    sortState={sortState[curriculumId] || { column: '', direction: 'asc' }} 
                                    onSort={(col) => handleSort(curriculumId, col)}
                                    style={{
                                        background: 'none !important',
                                        backgroundColor: 'transparent !important',
                                        padding: '1rem 1rem',
                                        textAlign: 'left',
                                        border: 'none',
                                        borderRight: 'none',
                                        fontSize: '0.95rem',
                                        letterSpacing: '0.75px',
                                        position: 'relative',
                                        textTransform: 'uppercase',
                                        fontFamily: 'system-ui, -apple-system, sans-serif',
                                        transition: 'none !important'
                                    }}
                                />
                            )}
                            {visibleColumns.includes('copreceptor') && (
                                <SortableTableHeader 
                                    label="Co-Preceptor" 
                                    columnKey="copreceptor" 
                                    sortState={sortState[curriculumId] || { column: '', direction: 'asc' }} 
                                    onSort={(col) => handleSort(curriculumId, col)}
                                    style={{
                                        background: 'none !important',
                                        backgroundColor: 'transparent !important',
                                        padding: '1rem 1rem',
                                        textAlign: 'left',
                                        border: 'none',
                                        borderRight: 'none',
                                        fontSize: '0.95rem',
                                        letterSpacing: '0.75px',
                                        position: 'relative',
                                        textTransform: 'uppercase',
                                        fontFamily: 'system-ui, -apple-system, sans-serif',
                                        transition: 'none !important'
                                    }}
                                />
                            )}
                            {visibleColumns.includes('email') && (
                                <SortableTableHeader 
                                    label="Email" 
                                    columnKey="email" 
                                    sortState={sortState[curriculumId] || { column: '', direction: 'asc' }} 
                                    onSort={(col) => handleSort(curriculumId, col)}
                                    style={{
                                        background: 'none !important',
                                        backgroundColor: 'transparent !important',
                                        padding: '1rem 1rem',
                                        textAlign: 'left',
                                        border: 'none',
                                        borderRight: 'none',
                                        fontSize: '0.95rem',
                                        letterSpacing: '0.75px',
                                        position: 'relative',
                                        textTransform: 'uppercase',
                                        fontFamily: 'system-ui, -apple-system, sans-serif',
                                        transition: 'none !important'
                                    }}
                                />
                            )}
                            {visibleColumns.includes('specialty') && (
                                <SortableTableHeader 
                                    label="Experience" 
                                    columnKey="specialty" 
                                    sortState={sortState[curriculumId] || { column: '', direction: 'asc' }} 
                                    onSort={(col) => handleSort(curriculumId, col)}
                                    style={{
                                        background: 'none !important',
                                        backgroundColor: 'transparent !important',
                                        padding: '1rem 1rem',
                                        textAlign: 'left',
                                        border: 'none',
                                        borderRight: 'none',
                                        fontSize: '0.95rem',
                                        letterSpacing: '0.75px',
                                        position: 'relative',
                                        textTransform: 'uppercase',
                                        fontFamily: 'system-ui, -apple-system, sans-serif',
                                        transition: 'none !important'
                                    }}
                                />
                            )}
                            {visibleColumns.includes('startDate') && (
                                <SortableTableHeader 
                                    label="Start Date" 
                                    columnKey="startDate" 
                                    sortState={sortState[curriculumId] || { column: '', direction: 'asc' }} 
                                    onSort={(col) => handleSort(curriculumId, col)}
                                    style={{
                                        background: 'none !important',
                                        backgroundColor: 'transparent !important',
                                        padding: '1rem 1rem',
                                        textAlign: 'left',
                                        border: 'none',
                                        borderRight: 'none',
                                        fontSize: '0.95rem',
                                        letterSpacing: '0.75px',
                                        position: 'relative',
                                        textTransform: 'uppercase',
                                        fontFamily: 'system-ui, -apple-system, sans-serif',
                                        transition: 'none !important'
                                    }}
                                />
                            )}
                            {visibleColumns.includes('endDate') && (
                                <SortableTableHeader 
                                    label="End Date" 
                                    columnKey="endDate" 
                                    sortState={sortState[curriculumId] || { column: '', direction: 'asc' }} 
                                    onSort={(col) => handleSort(curriculumId, col)}
                                    style={{
                                        background: 'none !important',
                                        backgroundColor: 'transparent !important',
                                        padding: '1rem 1rem',
                                        textAlign: 'left',
                                        border: 'none',
                                        borderRight: 'none',
                                        fontSize: '0.95rem',
                                        letterSpacing: '0.75px',
                                        position: 'relative',
                                        textTransform: 'uppercase',
                                        fontFamily: 'system-ui, -apple-system, sans-serif',
                                        transition: 'none !important'
                                    }}
                                />
                            )}
                        </tr>
                    </thead>
                    <tbody>
                        {getSortedData(rotations, curriculumId).map((rotation, index) => {
                            const dates = getRotationDates(rotation);
                            return (
                                <tr key={`${curriculumId}-${index}`} className={index % 2 === 0 ? 'even-row' : 'odd-row'}>
                                    {visibleColumns.includes('rotation') && <td>Rotation {rotation.rotation + 1}</td>}
                                    {visibleColumns.includes('preceptor') && <td>Dr. {rotation.preceptor.name}</td>}
                                    {visibleColumns.includes('copreceptor') && <td>{rotation.copreceptor ? `Dr. ${rotation.copreceptor.name}` : <OffBadge />}</td>}
                                    {visibleColumns.includes('email') && <td>{rotation.preceptor.email}</td>}
                                    {visibleColumns.includes('specialty') && <td>{rotation.speciality}</td>}
                                    {visibleColumns.includes('startDate') && <td>{dates.start}</td>}
                                    {visibleColumns.includes('endDate') && <td>{dates.end}</td>}
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        );
    };

    // Remove the formatAcademicYear function since we don't need it anymore

    // Handle export functionality
    const handleExport = (format) => {
        try {
            const dataToExport = startDate && endDate ? schedulesByCurriculum : 
                                selectedCurriculum ? { [selectedCurriculum]: schedulesByCurriculum[selectedCurriculum] } :
                                schedulesByCurriculum;
                                
            if (Object.keys(dataToExport).length === 0) {
                showError('No schedule data to export.');
                return;
            }

            // Create the actual rendered data structure for export
            const renderedData = [];
            
            Object.entries(dataToExport).forEach(([curriculumId, rotations]) => {
                rotations.forEach((rotation, index) => {
                    const dates = getRotationDates(rotation);
                    const rowData = {
                        'Curriculum': curriculumId,
                        'Rotation': `Rotation ${rotation.rotation + 1}`,
                        'Preceptor': rotation.preceptor ? `Dr. ${rotation.preceptor.name}` : '',
                        'Co-Preceptor': rotation.copreceptor ? `Dr. ${rotation.copreceptor.name}` : 'Off',
                        'Email': rotation.preceptor ? rotation.preceptor.email : '',
                        'Experience': rotation.speciality || '',
                        'Start Date': dates.start,
                        'End Date': dates.end
                    };
                    renderedData.push(rowData);
                });
            });

            const columns = [
                { key: 'Curriculum', label: 'Curriculum' },
                { key: 'Rotation', label: 'Rotation' },
                { key: 'Preceptor', label: 'Preceptor' },
                { key: 'Co-Preceptor', label: 'Co-Preceptor' },
                { key: 'Email', label: 'Email' },
                { key: 'Experience', label: 'Experience' },
                { key: 'Start Date', label: 'Start Date' },
                { key: 'End Date', label: 'End Date' }
            ];

            const title = 'Student Schedule';
            const filename = `student_schedule_${new Date().toISOString().split('T')[0]}`;
            
            exportRenderedTableData(renderedData, columns, title, filename, format);
        } catch (error) {
            console.error('Export failed:', error);
            showError('Export failed. Please try again.');
        }
    };

    // Column toggle handler for specific tables
    const handleColumnToggle = (curriculumId, column) => {
        setVisibleColumnsByTable(prev => {
            const currentColumns = prev[curriculumId] || availableColumns;
            const updatedColumns = currentColumns.includes(column)
                ? currentColumns.filter(col => col !== column)
                : [...currentColumns, column];
            
            return {
                ...prev,
                [curriculumId]: updatedColumns
            };
        });
    };

    // Handle table export for specific curriculum
    const handleTableExport = (curriculumId, format = 'CSV') => {
        try {
            const curriculumData = schedulesByCurriculum[curriculumId] || originalScheduleData[curriculumId];
            const visibleColumns = visibleColumnsByTable[curriculumId] || availableColumns;
            
            if (!curriculumData || curriculumData.length === 0) {
                showError('No data available for this table.');
                return;
            }

            // Create rendered data that matches exactly what's shown in the table
            const renderedData = [];
            
            curriculumData.forEach((rotation, index) => {
                const dates = getRotationDates(rotation);
                const rowData = {};
                
                // Only include data for visible columns
                if (visibleColumns.includes('rotation')) {
                    rowData['Rotation'] = `Rotation ${rotation.rotation + 1}`;
                }
                if (visibleColumns.includes('preceptor')) {
                    rowData['Preceptor'] = rotation.preceptor ? `Dr. ${rotation.preceptor.name}` : '';
                }
                if (visibleColumns.includes('copreceptor')) {
                    rowData['Co-Preceptor'] = rotation.copreceptor ? `Dr. ${rotation.copreceptor.name}` : 'Off';
                }
                if (visibleColumns.includes('email')) {
                    rowData['Email'] = rotation.preceptor ? rotation.preceptor.email : '';
                }
                if (visibleColumns.includes('specialty')) {
                    rowData['Experience'] = rotation.speciality || '';
                }
                if (visibleColumns.includes('startDate')) {
                    rowData['Start Date'] = dates.start;
                }
                if (visibleColumns.includes('endDate')) {
                    rowData['End Date'] = dates.end;
                }
                
                renderedData.push(rowData);
            });

            // Create columns structure for export
            const exportColumns = visibleColumns.map(colKey => {
                const columnLabels = {
                    'rotation': 'Rotation',
                    'preceptor': 'Preceptor',
                    'copreceptor': 'Co-Preceptor',
                    'email': 'Email',
                    'specialty': 'Experience',
                    'startDate': 'Start Date',
                    'endDate': 'End Date'
                };
                const label = columnLabels[colKey] || colKey;
                return { key: label, label: label };
            });

            const title = `Student Schedule - ${getCurriculumDisplayName(curriculumId)}`;
            const filename = `student_schedule_${curriculumId}_${new Date().toISOString().split('T')[0]}`;
            
            exportRenderedTableData(renderedData, exportColumns, title, filename, format);
        } catch (error) {
            console.error('Table export failed:', error);
            showError('Export failed. Please try again.');
        }
    };

    // Clear all filters
    const clearAllFilters = () => {
        setSelectedCurriculum('');
        setStartDate('');
        setEndDate('');
        setSchedulesByCurriculum(originalScheduleData);
        setAvailableCurriculums(Object.keys(originalScheduleData));
    };

    if (loading) {
        return (
            <div style={{ width: '100%', padding: 0, margin: 0 }}>
                <LoadingSpinner text="Loading schedules..." variant="page" />
            </div>
        );
    }

    const totalCurriculumsLoaded = Object.keys(originalScheduleData).length;
    const visibleCurriculums = Object.keys(schedulesByCurriculum).length;
    const visibleRotations = Object.values(schedulesByCurriculum).reduce(
        (sum, rotations) => sum + (Array.isArray(rotations) ? rotations.length : 0),
        0
    );
    const activeFilters = Number(Boolean(selectedCurriculum)) + Number(Boolean(startDate || endDate));

    return (
        <div className="vs-shell" style={{ width: '100%', padding: 0, margin: 0 }}>
            <div className="modern-schedule-header">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                    <div>
                        <h1 className="page-title">My Schedule</h1>
                        <p className="schedule-subtitle">View and manage your academic schedule</p>
                    </div>
                    <ExportButton 
                        onExport={handleExport}
                        formats={['PDF', 'CSV']}
                        disabled={loading || Object.keys(getFilteredRotations()).length === 0}
                        customLabel="Export Showing Tables"
                    />
                </div>
            </div>

            <div className="vs-kpi-grid">
                <div className="vs-kpi-card">
                    <p className="vs-kpi-value">{totalCurriculumsLoaded}</p>
                    <p className="vs-kpi-label">Available curriculums</p>
                </div>
                <div className="vs-kpi-card">
                    <p className="vs-kpi-value">{visibleCurriculums}</p>
                    <p className="vs-kpi-label">Visible curriculums</p>
                </div>
                <div className="vs-kpi-card">
                    <p className="vs-kpi-value">{visibleRotations}</p>
                    <p className="vs-kpi-label">Visible rotations</p>
                </div>
                <div className="vs-kpi-card">
                    <p className="vs-kpi-value">{activeFilters}</p>
                    <p className="vs-kpi-label">Active filters</p>
                </div>
            </div>
            
            <div className="vs-content-card" style={{ padding: 0, margin: 0 }}>
                
                {error && (
                    <div className="notification error-notification" role="alert">
                        <span className="notification-icon">⚠️</span>
                        <div className="notification-content">{error}</div>
                    </div>
                )}
                
                <div className="modern-schedule-controls">
                        <div className="filter-section" style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
                            gap: '1.5rem',
                            padding: '2rem',
                            background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
                            borderRadius: '16px',
                            border: '1px solid #e2e8f0',
                            boxShadow: '0 4px 16px rgba(0, 0, 0, 0.08)',
                            marginBottom: '2rem'
                        }}>
                            <div className="filter-group" style={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '0.75rem'
                            }}>
                                <label style={{
                                    fontSize: '0.95rem',
                                    fontWeight: '600',
                                    color: '#374151',
                                    letterSpacing: '0.25px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '0.5rem'
                                }}>
                                    <span style={{ fontSize: '1.1rem' }}>📚</span>
                                    Filter by Curriculum
                                </label>
                                <select
                                    value={selectedCurriculum}
                                    onChange={handleCurriculumChange}
                                    disabled={Object.keys(originalScheduleData).length === 0}
                                    style={{
                                        padding: '12px 16px',
                                        border: '2px solid #e5e7eb',
                                        borderRadius: '12px',
                                        fontSize: '0.95rem',
                                        fontWeight: '500',
                                        color: '#374151',
                                        background: '#fff',
                                        cursor: 'pointer',
                                        transition: 'all 0.2s ease',
                                        outline: 'none',
                                        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)'
                                    }}
                                    onFocus={(e) => {
                                        e.target.style.borderColor = '#3b82f6';
                                        e.target.style.boxShadow = '0 0 0 3px rgba(59, 130, 246, 0.1)';
                                    }}
                                    onBlur={(e) => {
                                        e.target.style.borderColor = '#e5e7eb';
                                        e.target.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.05)';
                                    }}
                                >
                                    {Object.keys(originalScheduleData).length === 0 ? (
                                        <option value="">No curriculums found</option>
                                    ) : (
                                        <>
                                    <option value="">Select curriculum</option>
                                    {Object.keys(originalScheduleData).map(curriculumId => (
                                        <option key={curriculumId} value={curriculumId}>
                                            {getCurriculumDisplayName(curriculumId)}
                                        </option>
                                    ))}
                                        </>
                                    )}
                                </select>
                            </div>

                            <div className="filter-group" style={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '0.75rem',
                                gridColumn: 'span 2'
                            }}>
                                <label style={{
                                    fontSize: '0.95rem',
                                    fontWeight: '600',
                                    color: '#374151',
                                    letterSpacing: '0.25px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '0.5rem'
                                }}>
                                    <span style={{ fontSize: '1.1rem' }}>📅</span>
                                    Date Range Filter
                                    <span style={{
                                        fontSize: '0.8rem',
                                        fontWeight: '400',
                                        color: '#6b7280',
                                        fontStyle: 'italic'
                                    }}>
                                        (Alternative to curriculum selection)
                                    </span>
                                </label>
                                <div style={{
                                    display: 'flex',
                                    gap: '0.75rem',
                                    alignItems: 'center'
                                }}>
                                    <input
                                        type="date"
                                        value={startDate}
                                        onChange={handleStartDateChange}
                                        disabled={!selectedCurriculum}
                                        style={{
                                            flex: 1,
                                            padding: '12px 16px',
                                            border: '2px solid #e5e7eb',
                                            borderRadius: '12px',
                                            fontSize: '0.95rem',
                                            fontWeight: '500',
                                            color: '#374151',
                                            background: '#fff',
                                            cursor: 'pointer',
                                            transition: 'all 0.2s ease',
                                            outline: 'none',
                                            boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)'
                                        }}
                                        onFocus={(e) => {
                                            e.target.style.borderColor = '#3b82f6';
                                            e.target.style.boxShadow = '0 0 0 3px rgba(59, 130, 246, 0.1)';
                                        }}
                                        onBlur={(e) => {
                                            e.target.style.borderColor = '#e5e7eb';
                                            e.target.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.05)';
                                        }}
                                    />
                                    <span style={{
                                        color: '#6b7280',
                                        fontWeight: '600',
                                        fontSize: '0.9rem',
                                        whiteSpace: 'nowrap'
                                    }}>
                                        to
                                    </span>
                                    <input
                                        type="date"
                                        value={endDate}
                                        onChange={handleEndDateChange}
                                        min={startDate}
                                        disabled={!selectedCurriculum}
                                        style={{
                                            flex: 1,
                                            padding: '12px 16px',
                                            border: '2px solid #e5e7eb',
                                            borderRadius: '12px',
                                            fontSize: '0.95rem',
                                            fontWeight: '500',
                                            color: '#374151',
                                            background: '#fff',
                                            cursor: 'pointer',
                                            transition: 'all 0.2s ease',
                                            outline: 'none',
                                            boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)'
                                        }}
                                        onFocus={(e) => {
                                            e.target.style.borderColor = '#3b82f6';
                                            e.target.style.boxShadow = '0 0 0 3px rgba(59, 130, 246, 0.1)';
                                        }}
                                        onBlur={(e) => {
                                            e.target.style.borderColor = '#e5e7eb';
                                            e.target.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.05)';
                                        }}
                                    />
                                </div>
                            </div>

                            {(selectedCurriculum || startDate || endDate) && (
                                <div style={{
                                    display: 'flex',
                                    alignItems: 'flex-end',
                                    justifyContent: 'center'
                                }}>
                                    <button
                                        onClick={clearAllFilters}
                                        style={{
                                            padding: '12px 24px',
                                            background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                                            border: 'none',
                                            borderRadius: '12px',
                                            color: '#fff',
                                            fontSize: '0.9rem',
                                            fontWeight: '600',
                                            cursor: 'pointer',
                                            transition: 'all 0.2s ease',
                                            boxShadow: '0 4px 12px rgba(239, 68, 68, 0.3)',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '0.5rem',
                                            letterSpacing: '0.25px'
                                        }}
                                        onMouseEnter={(e) => {
                                            e.target.style.transform = 'translateY(-2px)';
                                            e.target.style.boxShadow = '0 6px 20px rgba(239, 68, 68, 0.4)';
                                        }}
                                        onMouseLeave={(e) => {
                                            e.target.style.transform = 'translateY(0)';
                                            e.target.style.boxShadow = '0 4px 12px rgba(239, 68, 68, 0.3)';
                                        }}
                                    >
                                        <span>🗑️</span>
                                        Clear Filters
                                    </button>
                                </div>
                            )}
                        </div>
                </div>

                <div className="schedule-results">
                    {startDate && endDate ? (
                        // Show filtered results across all curriculums that match the date range
                        Object.keys(schedulesByCurriculum).length > 0 ? (
                            <div>
                                <div style={{ 
                                    marginBottom: '1.5rem', 
                                    padding: '1rem', 
                                    background: '#f8f9fa', 
                                    borderRadius: '8px', 
                                    color: '#495057',
                                    textAlign: 'center',
                                    border: '1px solid #e9ecef'
                                }}>
                                    <h3 style={{ margin: 0, fontSize: '1.2rem', fontWeight: '700' }}>
                                        📅 Filtered Results for Date Range: {formatDate(startDate)} - {formatDate(endDate)}
                                    </h3>
                                    <p style={{ margin: '0.5rem 0 0 0', fontSize: '0.9rem', opacity: 0.9 }}>
                                        Showing {Object.keys(schedulesByCurriculum).length} curriculum(s) with matching rotations
                                    </p>
                                </div>
                                {Object.keys(schedulesByCurriculum)
                                    .sort() // Sort curriculum IDs for consistent ordering
                                    .map(curriculumId => (
                                        <div key={curriculumId} style={{ marginBottom: '2rem' }}>
                                            {renderCurriculumSection(curriculumId, schedulesByCurriculum[curriculumId])}
                                        </div>
                                    ))}
                            </div>
                        ) : (
                            <div className="no-schedule-message">
                                <div style={{ 
                                    padding: '2rem', 
                                    textAlign: 'center', 
                                    background: '#fef2f2', 
                                    border: '2px dashed #fca5a5', 
                                    borderRadius: '12px',
                                    color: '#991b1b'
                                }}>
                                    <span style={{ fontSize: '3rem', marginBottom: '1rem', display: 'block' }}>📅❌</span>
                                    <h3 style={{ margin: '0 0 1rem 0', fontSize: '1.3rem', fontWeight: '700' }}>
                                        No Schedules Found
                                    </h3>
                                    <p style={{ margin: '0 0 1rem 0', fontSize: '1rem' }}>
                                        No schedules found for the selected date range:
                                    </p>
                                    <p style={{ margin: '0 0 1rem 0', fontSize: '1.1rem', fontWeight: '600' }}>
                                        {formatDate(startDate)} - {formatDate(endDate)}
                                    </p>
                                    <p style={{ margin: 0, fontSize: '0.9rem', opacity: 0.8 }}>
                                        Try adjusting your date range or clear the filters to see all schedules.
                                    </p>
                                </div>
                            </div>
                        )
                    ) : (() => {
                        // Determine what to show based on curriculum selection
                        const dataToShow = selectedCurriculum && originalScheduleData[selectedCurriculum] 
                                ? { [selectedCurriculum]: originalScheduleData[selectedCurriculum] }
                                : originalScheduleData;
                        
                        if (Object.keys(dataToShow).length > 0) {
                            let headerText = '';
                            let subText = '';
                            
                            if (selectedCurriculum && selectedCurriculum !== '') {
                                headerText = `🎯 Curriculum Filter: ${getCurriculumDisplayName(selectedCurriculum)}`;
                                subText = 'Showing selected curriculum';
                            } else {
                                headerText = '📚 All Curriculums - Complete Schedule Overview';
                                subText = `Showing all ${Object.keys(dataToShow).length} curriculum(s)`;
                            }
                            
                            return (
                                <div>
                                    <div style={{ 
                                        marginBottom: '1.5rem', 
                                        padding: '1.25rem', 
                                        background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)', 
                                        borderRadius: '16px', 
                                        color: '#1e293b',
                                        textAlign: 'center',
                                        border: '1px solid #cbd5e1',
                                        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                                    }}>
                                        <h3 style={{ margin: 0, fontSize: '1.3rem', fontWeight: '700', letterSpacing: '-0.025em' }}>
                                            {headerText}
                                        </h3>
                                        <p style={{ margin: '0.75rem 0 0 0', fontSize: '0.95rem', opacity: 0.8, fontWeight: '500' }}>
                                            {subText}
                                        </p>
                                    </div>
                                    {Object.keys(dataToShow)
                                        .sort() // Sort curriculum IDs for consistent ordering
                                        .map(curriculumId => (
                                            <div key={curriculumId} style={{ marginBottom: '2rem' }}>
                                                {renderCurriculumSection(curriculumId, dataToShow[curriculumId])}
                                            </div>
                                        ))}
                                </div>
                            );
                        } else {
                            return (
                                <div className="no-schedule-message">
                                    <div style={{ 
                                        padding: '2rem', 
                                        textAlign: 'center', 
                                        background: '#f8f9fa', 
                                        border: '2px dashed #dee2e6', 
                                        borderRadius: '12px',
                                        color: '#6c757d'
                                    }}>
                                        <span style={{ fontSize: '3rem', marginBottom: '1rem', display: 'block' }}>📚</span>
                                        <h3 style={{ margin: '0 0 1rem 0', fontSize: '1.3rem', fontWeight: '700' }}>
                                            No Schedule Data Available
                                        </h3>
                                        <p style={{ margin: 0, fontSize: '1rem' }}>
                                            There are no schedules available to display at this time.
                                        </p>
                                    </div>
                                </div>
                            );
                        }
                    })()}
                </div>
            </div>
        </div>
    );
};

export default ViewStudentSchedule;
